import { useI18n } from '../../context/I18nContext';
import SingleCableVisualization from './SingleCableVisualization';
import MultipleCableVisualization from './MultipleCableVisualization';
import MultipolarCableVisualization from './MultipolarCableVisualization';
import SheathCableVisualization from './SheathCableVisualization';
import { analyzeAndGroupCables } from '../../utils/cableLogic';


// ==================== COMPONENTE VISUALIZZAZIONE IBRIDA ====================

const HybridCableVisualization = ({ cables, settings, siglatura, hoveredCable, setHoveredCable, isMobile, availableSheaths = [] }) => {
    const { t } = useI18n();

    // ✅ STEP 1: Separa multipolari e standard
    const multipolarCables = cables.filter(cable => cable.poli && Array.isArray(cable.poli));
    const standardCables = cables.filter(cable => !(cable.poli && Array.isArray(cable.poli)));

    // ✅ STEP 2: Analizza i cavi standard per determinare multipli/singoli
    const allSubGroups = analyzeAndGroupCables(standardCables, settings);

    // ✅ STEP 3: Costruisci una lista unificata di elementi da renderizzare, ordinata per originalIndex
    const renderItems = [];

    // Aggiungi i multipolari come elementi individuali
    multipolarCables.forEach(cable => {
        renderItems.push({
            type: 'multipolar',
            sortIndex: cable.originalIndex,
            cable: cable,
            key: `multipolar-${cable.originalIndex}`
        });
    });

    // Aggiungi i gruppi multipli
    allSubGroups.forEach((group, groupIdx) => {
        if (!group.isSingle) {
            const firstIndex = Math.min(...group.cables.map(c => c.originalIndex));
            renderItems.push({
                type: 'multiple',
                sortIndex: firstIndex,
                group: group,
                groupIdx: groupIdx,
                key: `multi-${firstIndex}`
            });
        }
    });

    // Raccogli i singoli con guaina raggruppati per sheathId
    const sheathGroups = {};
    const singleCablesWithoutSheath = [];

    allSubGroups.forEach(group => {
        if (group.isSingle) {
            const cable = group.cables[0];
            if (cable.guaina) {
                if (!sheathGroups[cable.guaina]) {
                    sheathGroups[cable.guaina] = [];
                }
                sheathGroups[cable.guaina].push(cable);
            } else {
                singleCablesWithoutSheath.push(cable);
            }
        }
    });

    // Aggiungi i gruppi guaina
    Object.entries(sheathGroups).forEach(([sheathId, cablesToRender]) => {
        const firstIndex = Math.min(...cablesToRender.map(c => c.originalIndex));
        renderItems.push({
            type: 'sheath',
            sortIndex: firstIndex,
            sheathId: sheathId,
            cables: cablesToRender,
            key: `sheath-${sheathId}`
        });
    });

    // Aggiungi i cavi singoli senza guaina
    singleCablesWithoutSheath.forEach(cable => {
        renderItems.push({
            type: 'single',
            sortIndex: cable.originalIndex,
            cable: cable,
            key: `single-${cable.originalIndex}`
        });
    });

    // ✅ STEP 4: Ordina per originalIndex per mantenere l'ordine naturale
    renderItems.sort((a, b) => a.sortIndex - b.sortIndex);

    // ✅ STEP 5: Renderizza in ordine
    return (
        <div className="space-y-4">
            {renderItems.map((item, idx) => {
                if (item.type === 'multipolar') {
                    return (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-2" data-cable-index={item.cable.originalIndex}>
                            <MultipolarCableVisualization
                                cable={item.cable}
                                hoveredCable={hoveredCable}
                                setHoveredCable={setHoveredCable}
                                isMobile={isMobile}
                                settings={settings}
                            />
                        </div>
                    );
                }

                if (item.type === 'multiple') {
                    const cablesWithSheath = item.group.cables.filter(c => c.guaina);
                    const sheathId = cablesWithSheath.length > 0 ? cablesWithSheath[0].guaina : null;
                    const sheath = sheathId ? availableSheaths.find(s => Number(s.id) === Number(sheathId)) : null;

                    return (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-2" data-cable-indices={item.group.cables.map(c => c.originalIndex).join(',')}>
                            <MultipleCableVisualization
                                cables={item.group.cables}
                                siglatura={`${siglatura} (${t('schematic.group')} ${item.groupIdx + 1})`}
                                hoveredCable={hoveredCable}
                                setHoveredCable={setHoveredCable}
                                isMobile={isMobile}
                                settings={settings}
                                sheath={sheath}
                                availableSheaths={availableSheaths}
                            />
                        </div>
                    );
                }

                if (item.type === 'sheath') {
                    const sheath = availableSheaths.find(s => s.id === parseInt(item.sheathId));
                    const sheathLabel = sheath ? (sheath.fullLabel || sheath.label) : `${t('schematic.sheath')} ${item.sheathId}`;

                    return (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-2" data-sheath-id={item.sheathId}>
                            <SheathCableVisualization
                                cables={item.cables}
                                sheathLabel={sheathLabel}
                                sheathType={sheath?.type}
                                hoveredCable={hoveredCable}
                                setHoveredCable={setHoveredCable}
                                isMobile={isMobile}
                                settings={settings}
                            />
                        </div>
                    );
                }

                if (item.type === 'single') {
                    return (
                        <div key={item.key} className="bg-gray-50 rounded-lg p-2" data-cable-index={item.cable.originalIndex}>
                            <SingleCableVisualization
                                cable={item.cable}
                                hoveredCable={hoveredCable}
                                setHoveredCable={setHoveredCable}
                                isMobile={isMobile}
                                settings={settings}
                            />
                        </div>
                    );
                }

                return null;
            })}
        </div>
    );
};

export default HybridCableVisualization;